import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Me } from "@workcore/schemas";
import { get } from "./api";
import { completeSignIn, identity, shouldSignInSilently, signIn, signOut, SilentSignInRefused } from "./auth";
import { usePlatformMe, useSignedIn } from "./session";
import { BrandMark, ErrorBox, ToastHost } from "./ui";
import { WorkspacesPage } from "./pages/Workspaces";
import { WorkspaceDetailPage } from "./pages/WorkspaceDetail";
import { MyWorkspacePage } from "./pages/MyWorkspace";
import { UsersPage } from "./pages/Users";
import { GroupsPage } from "./pages/Groups";
import { RolesPage } from "./pages/Roles";
import { ServiceAccountsPage } from "./pages/ServiceAccounts";
import { WorkflowsPage } from "./pages/Workflows";
import { WorkflowEditorPage } from "./pages/WorkflowEditor";
import { ProjectsPage } from "./pages/projects/Projects";
import { AccessPage } from "./pages/projects/Access";
import { StructurePage } from "./pages/projects/Structure";
import { PeriodsPage } from "./pages/projects/Periods";
import { ProjectContactsPage } from "./pages/projects/ProjectContacts";
import { ContactsPage } from "./pages/contacts/Contacts";
import { OperatorsPage } from "./pages/Operators";
import { WebhooksPage } from "./pages/Webhooks";
import { NotificationsPage } from "./pages/Notifications";
import { MailPage } from "./pages/Mail";
import { MoneySettingsPage } from "./pages/money/MoneySettings";
import { ProjectMoneyPage } from "./pages/projects/ProjectMoney";
import { AuditLogPage } from "./activity";
import { cfg } from "./config";
import { Bot, Building2, FolderKanban, KeyRound, LogOut, ScrollText, ShieldCheck, Users, UsersRound, Webhook, Workflow, type LucideIcon, Contact2, Bell, Mail, Coins } from "lucide-react";
import { ConfirmHost } from "./confirm";

function Callback() {
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>();
  useEffect(() => {
    completeSignIn(window.location.search)
      .then((to) => navigate(to, { replace: true }))
      .catch((e: unknown) => (e instanceof SilentSignInRefused ? navigate(e.returnTo, { replace: true }) : setError(e)));
  }, [navigate]);
  return error ? (
    <SignInCard>
      <h1>Sign-in did not finish</h1>
      <ErrorBox error={error} />
      <button className="wide" onClick={() => void signIn("/")}>Sign in again</button>
    </SignInCard>
  ) : (
    <p className="centre muted">Signing in…</p>
  );
}

/** Signed in, but nowhere to go. Shows the sign-in identity, which is what an operator grant or user add needs. */
function NotYetAMember() {
  const id = identity();
  return (
    <>
      <p>You are signed in but not a member of any workspace, and not a platform operator. Ask an administrator for an invitation.</p>
      {id && (
        <dl className="facts">
          <dt>Issuer</dt>
          <dd><code>{id.iss}</code></dd>
          <dt>Subject</dt>
          <dd><code>{id.sub}</code></dd>
          <dt>Email</dt>
          <dd>{id.email ?? "not in the token"}</dd>
        </dl>
      )}
    </>
  );
}

function SignInScreen() {
  const location = useLocation();
  // Tried once per render cycle at most; shouldSignInSilently records the attempt so a refusal cannot loop.
  const [silent] = useState(shouldSignInSilently);
  useEffect(() => {
    if (silent) void signIn(undefined, { silent: true });
  }, [silent]);
  if (silent) return <p className="centre muted">Signing in…</p>;
  const issuer = cfg().issuer;
  const microsoft = /^https:\/\/login\.microsoftonline\.com\//.test(issuer);
  const go = () => void signIn(`${location.pathname}${location.search}${location.hash}`);
  return (
    <SignInCard>
      <h1>Sign in to workcore</h1>
      <p className="muted">Configure workspaces, workflows, users and access.</p>
      {microsoft ? (
        <button className="ms-signin" onClick={go}>
          <MicrosoftLogo /> Sign in with Microsoft
        </button>
      ) : (
        <button className="wide" onClick={go}>Sign in</button>
      )}
      <details className="trouble">
        <summary>Having trouble?</summary>
        <p>You sign in through <code>{issuer}</code>. If it says you are not a member, ask whoever runs your workspace to invite your work email.</p>
      </details>
    </SignInCard>
  );
}

/** The centred card used before there is a session: sign-in, callback errors, not-a-member. */
function SignInCard({ children }: { children: ReactNode }) {
  return (
    <div className="signin-page">
      <main className="signin-card">
        <div className="signin-brand">
          <BrandMark size={28} />
          <span className="word">workcore</span>
        </div>
        {children}
      </main>
      <footer className="signin-foot">
        workcore {__WORKCORE_VERSION__} · Riddings IT Ltd
      </footer>
    </div>
  );
}

/** Microsoft's four squares, for the standard "Sign in with Microsoft" button. */
function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

type NavItem = { to: string; label: string; icon: LucideIcon; show: boolean };

/** The workspace's sections, grouped, and only those the caller's roles there can use. */
function WorkspaceNav({ slug }: { slug: string }) {
  const me = useQuery({ queryKey: ["ws-me", slug], queryFn: async () => Me.parse(await get(`/v1/ws/${slug}/me`)) });
  const sys = me.data?.isSystemAdmin ?? false;
  const workflows = sys || (me.data?.roles.some((r) => r.role === "workflow_admin") ?? false);
  const projects = workflows || (me.data?.roles.some((r) => r.role === "project_admin") ?? false);
  const groups: { title: string; items: NavItem[] }[] = [
    {
      title: "Work",
      items: [
        { to: "projects", label: "Projects", icon: FolderKanban, show: projects },
        { to: "workflows", label: "Workflows", icon: Workflow, show: workflows },
        { to: "contacts", label: "Contacts", icon: Contact2, show: projects },
        { to: "money", label: "Money", icon: Coins, show: sys },
      ],
    },
    {
      title: "Access",
      items: [
        { to: "users", label: "Users", icon: Users, show: sys },
        { to: "groups", label: "Groups", icon: UsersRound, show: sys },
        { to: "roles", label: "Roles", icon: ShieldCheck, show: sys },
        { to: "service-accounts", label: "Service accounts", icon: Bot, show: sys },
      ],
    },
    { title: "Integrations", items: [{ to: "webhooks", label: "Webhooks", icon: Webhook, show: sys }, { to: "mail", label: "Email", icon: Mail, show: sys }] },
    { title: "Records", items: [{ to: "audit", label: "Audit log", icon: ScrollText, show: projects }] },
  ];
  return (
    <>
      {groups.map((g) => {
        const items = g.items.filter((i) => i.show);
        return items.length === 0 ? null : (
          <div className="nav-group" key={g.title}>
            <h3>{g.title}</h3>
            {items.map((i) => (
              <NavLink key={i.to} to={`/w/${slug}/${i.to}`} title={i.label}>
                <i.icon size={16} strokeWidth={1.5} aria-hidden="true" />
                <span>{i.label}</span>
              </NavLink>
            ))}
          </div>
        );
      })}
    </>
  );
}

/** Which workspace (or the platform) the sidebar is showing; changing it navigates there. */
function Switcher({ op, current }: { op: boolean; current: string | undefined }) {
  const me = usePlatformMe();
  const navigate = useNavigate();
  const value = current ? `w:${current}` : "platform";
  const members = me.data?.memberships ?? [];
  const invites = me.data?.invitations ?? [];
  if (!op && members.length + invites.length === 0) return null;
  return (
    <label className="switcher">
      <span className="sr-only">Workspace</span>
      <select value={value} onChange={(e) => navigate(e.target.value === "platform" ? "/platform/workspaces" : `/w/${e.target.value.slice(2)}`)}>
        {op && <option value="platform">Platform</option>}
        {!current && !op && <option value="platform">Choose a workspace…</option>}
        {members.length > 0 && (
          <optgroup label="My workspaces">
            {members.map((m) => <option key={m.slug} value={`w:${m.slug}`}>{m.name}</option>)}
          </optgroup>
        )}
        {invites.length > 0 && (
          <optgroup label="Invitations">
            {invites.map((i) => <option key={i.slug} value={`w:${i.slug}`}>Join {i.name}</option>)}
          </optgroup>
        )}
      </select>
    </label>
  );
}

/** Initials in a circle; the menu holds who you are and Sign out. */
function AvatarMenu({ op, current }: { op: boolean; current: string | undefined }) {
  const navigate = useNavigate();
  const id = identity();
  const queries = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => (document.removeEventListener("mousedown", close), document.removeEventListener("keydown", close));
  }, [open]);
  const name = id?.name ?? id?.email ?? id?.sub ?? "?";
  const initials = name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
  return (
    <div className="avatar-wrap" ref={ref}>
      <button className="avatar" aria-label="Account" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        {initials}
      </button>
      {open && (
        <div className="menu account" role="menu">
          <div className="account-who">
            <strong>{id?.name ?? id?.email}</strong>
            {id?.name && id.email && <span className="muted small">{id.email}</span>}
            {op && <span className="badge badge-operator">Platform operator</span>}
          </div>
          {current && (
            <button role="menuitem" onClick={() => (setOpen(false), navigate(`/w/${current}/notifications`))}>
              <Bell size={16} strokeWidth={1.5} aria-hidden="true" /> Notification settings
            </button>
          )}
          <button
            role="menuitem"
            onClick={() => {
              signOut({ forget: true });
              queries.clear();
            }}
          >
            <LogOut size={16} strokeWidth={1.5} aria-hidden="true" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Shell() {
  const me = usePlatformMe();
  const op = me.data?.isPlatformOperator ?? false;
  const current = useMatch("/w/:slug/*")?.params.slug;
  return (
    <div className="shell">
      <header className="top">
        <Link to="/" className="brand" aria-label="workcore console, home">
          <BrandMark />
          <span className="word">workcore</span>
          <span className="suffix">console</span>
        </Link>
        <AvatarMenu op={op} current={current} />
      </header>
      <nav className="side" aria-label="Sections">
        <Switcher op={op} current={current} />
        {!current && op && (
          <div className="nav-group">
            <h3>Platform</h3>
            <NavLink to="/platform/workspaces" title="Workspaces">
              <Building2 size={16} strokeWidth={1.5} aria-hidden="true" />
              <span>Workspaces</span>
            </NavLink>
            <NavLink to="/platform/operators" title="Operators">
              <KeyRound size={16} strokeWidth={1.5} aria-hidden="true" />
              <span>Operators</span>
            </NavLink>
          </div>
        )}
        {current && <WorkspaceNav slug={current} />}
        {!current && !op && (me.data?.memberships.length ?? 0) === 0 && <p className="muted small nav-empty">No workspaces yet</p>}
      </nav>
      <main>
        <ErrorBox error={me.error} />
        {me.data && (
          <Routes>
            <Route path="/" element={<Navigate to={op ? "/platform/workspaces" : me.data.memberships[0] ? `/w/${me.data.memberships[0].slug}` : me.data.invitations[0] ? `/w/${me.data.invitations[0].slug}` : "/none"} replace />} />
            <Route path="/platform/workspaces" element={op ? <WorkspacesPage /> : <Navigate to="/" replace />} />
            <Route path="/platform/workspaces/:slug" element={op ? <WorkspaceDetailPage /> : <Navigate to="/" replace />} />
            <Route path="/platform/operators" element={op ? <OperatorsPage /> : <Navigate to="/" replace />} />
            <Route path="/w/:slug" element={<MyWorkspacePage />} />
            <Route path="/w/:slug/webhooks" element={<WebhooksPage />} />
            <Route path="/w/:slug/mail" element={<MailPage />} />
            <Route path="/w/:slug/notifications" element={<NotificationsPage />} />
            <Route path="/w/:slug/audit" element={<AuditLogPage />} />
            <Route path="/w/:slug/users" element={<UsersPage />} />
            <Route path="/w/:slug/groups" element={<GroupsPage />} />
            <Route path="/w/:slug/roles" element={<RolesPage />} />
            <Route path="/w/:slug/service-accounts" element={<ServiceAccountsPage />} />
            <Route path="/w/:slug/workflows" element={<WorkflowsPage />} />
            <Route path="/w/:slug/workflows/:id" element={<WorkflowEditorPage />} />
            <Route path="/w/:slug/projects" element={<ProjectsPage />} />
            <Route path="/w/:slug/projects/:id/access" element={<AccessPage />} />
            <Route path="/w/:slug/projects/:id/structure" element={<StructurePage />} />
            <Route path="/w/:slug/projects/:id/periods" element={<PeriodsPage />} />
            <Route path="/w/:slug/projects/:id/contacts" element={<ProjectContactsPage />} />
            <Route path="/w/:slug/contacts" element={<ContactsPage />} />
            <Route path="/w/:slug/money" element={<MoneySettingsPage />} />
            <Route path="/w/:slug/projects/:id/money" element={<ProjectMoneyPage />} />
            <Route path="/none" element={<NotYetAMember />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
      <ConfirmHost />
      <ToastHost />
    </div>
  );
}

export function App() {
  const signedIn = useSignedIn();
  const location = useLocation();
  if (location.pathname === "/callback") return <Callback />;
  return signedIn ? <Shell /> : <SignInScreen />;
}
